import styled from 'styled-components';

import { gql } from '@apollo/client';
import { NextPage } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import Router from 'next/router';
import { FunctionComponent } from 'react';
import { TeamMapAnimation } from '../components/animations/team-map';
import { Footer, FOOTER_QUERY } from '../components/footer';
import { Header, HEADER_QUERY } from '../components/header';
import { Jobs } from '../components/jobs';
import { Col, ResponsiveContainer, Row } from '../components/responsive';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { ContentStackClient } from '../lib/contentstack';
import { getTheme } from '../theme/getTheme';
import { AboutUs } from '../types/contentstack';
import { IPageProps } from '../types/page';
import { getAssetUrl } from '../utils/contentstack';
import { mediaScreen } from '../utils/mediaScreen';

export const Page: NextPage<IPageProps<AboutUs>> = ({
  header,
  page,
  footer,
}) => {
  function getTitleText() {
    return page?.hero?.title?.replace(/(\[.*\]?)/g, '') || '';
  }

  return (
    <>
      <Header
        pageTitle={page?.title}
        pageDescription={page?.description}
        {...header}
      />

      <Hero>
        <WidthDelimiter>
          <ResponsiveContainer>
            <Row>
              <Col md={8}>
                <HeroTitle>
                  {getTitleText()}

                  <br />

                  <MappedKeyWords title={page?.hero?.title || ''} />
                </HeroTitle>

                <HeroText>{page?.hero?.text}</HeroText>

                {!!page?.hero?.button?.href && (
                  <Button
                    mColor="teal"
                    onClick={() => Router.push(page?.hero?.button?.href ?? '')}
                  >
                    {page?.hero?.button?.title}
                  </Button>
                )}
              </Col>
            </Row>
          </ResponsiveContainer>
        </WidthDelimiter>
      </Hero>

      {false && (
        <WidthDelimiter>
          <ResponsiveContainer id="team">
            <Separator />
            <SectionTitle>{page?.team?.title}</SectionTitle>

            <TeamMembers>
              <Row>
                {page?.team?.member?.map((member) => {
                  return (
                    <Col xs={6} md={4} className="member" key={member?.name}>
                      <div className="picture">
                        <div className="inner">
                          <div className="front">
                            <Image
                              width={120}
                              height={120}
                              alt={member?.name || ''}
                              src={getAssetUrl(member?.pictureConnection)}
                            />
                          </div>

                          <div
                            className="back"
                            onClick={() =>
                              window.open(member?.linkedin as string)
                            }
                          >
                            <p>{member?.name}</p>

                            <img
                              src="/img/linkedin_icon.svg"
                              width="18"
                              height="18"
                              alt="linkedin icon"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="details">
                        <h3>{member?.name}</h3>
                        <span>{member?.role}</span>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </TeamMembers>
          </ResponsiveContainer>
        </WidthDelimiter>
      )}

      <WidthDelimiter>
        <Localization>
          <ResponsiveContainer>
            <Row>
              <Col md={6}>
                <Separator size="tiny" />
                <SectionTitle>{page?.localization?.title}</SectionTitle>
                <SectionText>{page?.localization?.text}</SectionText>
              </Col>
            </Row>
          </ResponsiveContainer>

          <TeamMapAnimation />
        </Localization>
      </WidthDelimiter>

      <WidthDelimiter>
        <Diversity>
          <ResponsiveContainer>
            <Row>
              <Col md={6}>
                <img
                  width={347}
                  height={406}
                  className="cube"
                  src="/img/diversity.svg"
                  alt="diversity"
                />
              </Col>

              <Col md={6}>
                <Separator size="tiny" />
                <SectionTitle>{page?.diversity?.title}</SectionTitle>
                <SectionText>{page?.diversity?.text}</SectionText>
              </Col>
            </Row>
          </ResponsiveContainer>
        </Diversity>
      </WidthDelimiter>

      <Investors id="investors">
        <h3>{page?.investors?.title}</h3>

        <ResponsiveContainer>
          <div className="logos">
            {page?.investors?.logos?.map((logo) => {
              return (
                <img
                  key={logo?.url}
                  src={getAssetUrl(logo?.imageConnection)}
                  alt={logo?.url as string}
                  onClick={() => window.open(logo?.url as string, '_blank')}
                />
              );
            })}
          </div>
        </ResponsiveContainer>
      </Investors>

      <Careers id="careers">
        <Jobs
          header={
            <Col xs={12} md={7}>
              <SectionTitle>Careers</SectionTitle>
              <SectionText>
                We’re always looking to hire talented people with a variety of
                backgrounds and specialties. Don’t see the role you’re looking
                for?
                <span> </span>
                <Link href="/contact?purpose=6">Connect with us</Link> to start
                a conversation.
              </SectionText>
            </Col>
          }
        />
      </Careers>

      <Footer
        title={page?.footer?.title}
        text={page?.footer?.text}
        button={page?.footer?.button?.title}
        links={footer?.links}
        onButtonClick={() => Router.push(page?.footer?.button?.href ?? '')}
      />
    </>
  );
};

const MappedKeyWords: FunctionComponent<{ title: string }> = ({ title }) => {
  const { colors } = getTheme();

  if (!title?.includes('[people')) {
    return null;
  }

  return (
    <span style={{ color: 'white' }}>
      <span style={{ color: colors.yellow }}>people</span>,{' '}
      <span style={{ color: colors.eb2 }}>places</span>, and{' '}
      <span style={{ color: colors.teal }}>things</span>
    </span>
  );
};

export async function getServerSideProps() {
  try {
    const { data } = await ContentStackClient().query<IPageProps<AboutUs>>({
      query: PAGE_QUERY,
    });

    return { props: data };
  } catch (e) {
    return { props: {} };
  }
}

const PAGE_QUERY = gql`
  query aboutUsQuery {
    ${HEADER_QUERY}
    ${FOOTER_QUERY}

    page: about_us(uid: "blt984013d80721ef63") {
      title
      description
      hero {
        title
        text
        button {
          title
          href
        }
      }
      team {
        title
        member {
          name
          role
          linkedin
          pictureConnection {
            edges {
              node {
                url
              }
            }
          }
        }
      }
      localization {
        title
        text
      }
      diversity {
        title
        text
      }
      investors {
        title
        logos {
          url
          imageConnection {
            edges {
              node {
                url
              }
            }
          }
        }
      }
      footer {
        title
        text
        button {
          title
          href
        }
      }
    }
  }
`;

const WidthDelimiter = styled.div`
  max-width: 1600px;
  position: relative;
  margin: 0 auto;
`;

const Hero = styled.div`
  width: 100%;
  padding-top: 160px;
  padding-bottom: 140px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-size: 100%;

  ${mediaScreen('md')} {
    background: url(/img/mapped_mark_background.svg) no-repeat top right;
  }

  ${mediaScreen('xs', 'md')} {
    padding-bottom: 80px;
  }
`;

const HeroTitle = styled.h1`
  color: white;
  font-size: 67px;
  font-weight: bold;
  line-height: 1.19;
  margin-top: 0;

  strong {
    color: ${(props) => props.theme.colors.teal};
  }

  ${mediaScreen('xs', 'sm')} {
    font-size: 35px;
  }

  ${mediaScreen('sm', 'md')} {
    font-size: 50px;
  }

  .typing {
    display: inline-block;
  }
`;

const HeroText = styled.p`
  color: white;
  font-size: 22px;
  line-height: 1.5;
  margin-top: 50px;
  max-width: 85%;
  font-weight: 400;
  margin-bottom: 44px;

  ${mediaScreen('xs', 'sm')} {
    font-size: 17px;
    max-width: 100%;
    margin-top: 24px;
    margin-bottom: 32px;
  }

  ${mediaScreen('sm', 'md')} {
    font-size: 19px;
    margin-top: 24px;
    margin-bottom: 32px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 50px;
  line-height: 1.2;
  color: white;
  font-weight: bold;
  margin-top: 0;

  ${mediaScreen('xs', 'sm')} {
    font-size: 29px;
  }

  ${mediaScreen('sm', 'md')} {
    font-size: 40px;
  }
`;

const SectionText = styled.p`
  color: white;
  font-size: 22px;
  line-height: 30px;

  ${mediaScreen('xs', 'sm')} {
    font-size: 17px;
    line-height: 24px;
  }

  ${mediaScreen('sm', 'md')} {
    font-size: 19px;
    line-height: 25px;
  }

  a {
    text-decoration: underline;

    &,
    &:visited {
      color: white;
    }
  }
`;

const TeamMembers = styled.div`
  padding-top: 20px;
  padding-bottom: 140px;

  ${mediaScreen('xs', 'md')} {
    padding-bottom: 40px;
  }

  .member {
    display: flex;
    margin-bottom: 128px;

    ${mediaScreen('xs', 'sm')} {
      align-items: center;
      justify-content: center;

      margin-bottom: 100px;
    }

    .picture {
      width: 128px;
      height: 128px;
      perspective: 1000px;
      background: transparent;
      cursor: pointer;

      ${mediaScreen('xs', 'sm')} {
        width: 110px;
        height: 110px;
      }

      &:hover .inner {
        transform: rotateY(180deg);
      }

      .inner {
        position: relative;
        width: 100%;
        height: 100%;
        transition: transform 0.5s ease;
        transform-style: preserve-3d;

        .front,
        .back {
          position: absolute;
          width: 100%;
          height: 100%;
          border-radius: 100%;
          backface-visibility: hidden;
          display: flex;
          flex-direction: column;
          text-align: center;
          align-items: center;
          justify-content: center;

          > div {
            backface-visibility: hidden;
          }
        }

        .front {
          img {
            width: 100%;
            height: 100%;
            border-radius: 100%;
          }
        }

        .back {
          background: ${(props) => props.theme.colors.yellow};
          transform: rotateY(180deg);
          padding: 15%;
          font-size: 13px;
          font-weight: bold;
          color: ${(props) => props.theme.colors.slate1};

          img {
            margin-top: 6px;
          }

          ${mediaScreen('sm')} {
            p {
              display: none;
            }

            img {
              width: 36px;
              height: 36px;
              margin-top: 0;
            }
          }
        }
      }
    }

    .details {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
      padding-left: 20px;
      padding-right: 40px;

      h3 {
        margin: 0;
        margin-bottom: 5px;
      }

      ${mediaScreen('xs', 'sm')} {
        display: none;
      }
    }
  }
`;

const Localization = styled.div`
  width: 100%;
  position: relative;

  .team-map {
    position: absolute;
    right: 0;
    top: -20%;
    width: 48%;

    ${mediaScreen('xs', 'md')} {
      position: relative;
      width: 100%;
      top: 0;
      margin-top: 50px;
    }

    ${mediaScreen('md', 'lg')} {
      top: -5%;
    }

    ${mediaScreen('xl')} {
      top: -40%;
    }
  }
`;

const Diversity = styled.div`
  margin-top: 300px;

  .cube {
    max-width: 70%;
  }

  ${mediaScreen('xs', 'md')} {
    margin-top: 20%;

    .row {
      flex-direction: column-reverse;
    }

    .cube {
      max-width: 95%;
      margin: 0 auto;
      margin-top: 20%;
    }
  }

  ${mediaScreen('xl')} {
    margin-top: 450px;

    .cube {
      width: 70%;
      max-width: 70%;
      margin-top: -70px;
    }
  }
`;

const Investors = styled.div`
  background: ${(props) => props.theme.colors.slate2};
  padding-top: 80px;
  padding-bottom: 30px;
  display: flex;
  flex-direction: column;
  text-align: center;
  align-items: center;
  justify-content: center;
  margin-top: 150px;

  h3 {
    font-size: 17px;
    margin-top: 0;
    margin-bottom: 30px;
  }

  .logos {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    max-width: 1140px;
    margin: 0 auto;

    img {
      max-width: 250px;
      height: auto;
      margin: 50px;
      cursor: pointer;
      pointer-events: all;

      &:hover {
        opacity: 0.7;
      }
    }
  }

  ${mediaScreen('xs', 'md')} {
    padding: 60px;

    .logos {
      flex-direction: column;

      img {
        max-width: 180px;
        margin: 35px 0;
      }
    }
  }
`;

const Careers = styled(ResponsiveContainer)`
  margin-top: 10%;
  margin-bottom: 25%;

  .jobs {
    margin-top: 80px;
  }
`;

export default Page;
